export type FormatLength = "short" | "long";

interface ParsedDecimal {
  negative: boolean;
  digits: string;
  exponent: number;
  zero: boolean;
}

function parseDecimalString(value: string | null | undefined): ParsedDecimal | null {
  if (!value) return null;
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return null;
  const whole = match[2];
  const fraction = match.at(3) ?? "";
  const combined = whole + fraction;
  const first = combined.search(/[1-9]/);
  if (first < 0) return { negative: false, digits: "0", exponent: 0, zero: true };
  return {
    negative: match[1] === "-",
    digits: combined.slice(first),
    exponent: whole.length - first - 1,
    zero: false,
  };
}

function roundedSignificant(parsed: ParsedDecimal, significant: number) {
  if (parsed.zero) return { digits: "0", exponent: 0 };
  let digits = parsed.digits.slice(0, significant);
  let exponent = parsed.exponent;
  if ((parsed.digits[significant] ?? "0") >= "5") {
    digits = (BigInt(digits) + 1n).toString();
    if (digits.length > significant) {
      exponent++;
      digits = digits.slice(0, significant);
    }
  }
  return { digits: digits.replace(/0+$/, "") || "0", exponent };
}

function decimalNotation(digits: string, exponent: number) {
  const point = exponent + 1;
  if (point <= 0) return `0.${"0".repeat(-point)}${digits}`;
  if (point >= digits.length) return digits + "0".repeat(point - digits.length);
  return `${digits.slice(0, point)}.${digits.slice(point)}`;
}

function scientificNotation(digits: string, exponent: number) {
  const coefficient = digits.length === 1 ? digits : `${digits[0]}.${digits.slice(1)}`;
  return `${coefficient}e${String(exponent)}`;
}

function boundedDecimal(
  value: string | null | undefined,
  length: FormatLength,
  decimalFloor = -9,
  maxFraction?: number,
  significantOverride?: number,
): string | null {
  const parsed = parseDecimalString(value);
  if (!parsed) return null;
  const significant = significantOverride ?? (length === "short" ? 8 : 12);
  const availableAtFractionLimit =
    maxFraction === undefined || parsed.exponent < decimalFloor
      ? significant
      : parsed.exponent + 1 + maxFraction;
  const rounded = roundedSignificant(
    parsed,
    Math.max(1, Math.min(significant, availableAtFractionLimit)),
  );
  const sign = parsed.negative && !parsed.zero ? "-" : "";
  const decimal = decimalNotation(rounded.digits, rounded.exponent);
  const numericLength = sign.length + decimal.length;
  if (rounded.exponent < decimalFloor || rounded.exponent >= 9 || numericLength > 18)
    return sign + scientificNotation(rounded.digits, rounded.exponent);
  return sign + decimal;
}

function groupInteger(value: string) {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatMoney(value: string | null, currency = "USD") {
  if (currency === "USD") return formatFundingUsd(value);
  if (value === null || value === "") return "Unavailable";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Unavailable";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(numeric);
}

export function formatPercent(value: string | null) {
  if (value === null || value === "") return "Unavailable";
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(numeric)}%`
    : "Unavailable";
}

export function formatPrice(
  value: string | null | undefined,
  length: FormatLength = "short",
  _siblings: readonly string[] = [],
) {
  if (length === "long" && value) {
    for (let significant = 8; significant <= 15; significant++) {
      const formatted = boundedDecimal(value, length, -9, 15, significant);
      const collision = _siblings.some(
        (sibling) =>
          sibling !== value && boundedDecimal(sibling, length, -9, 15, significant) === formatted,
      );
      if (!collision) return formatted ?? "—";
    }
  }
  return boundedDecimal(value, length, -9, length === "short" ? 9 : 15) ?? "—";
}

export function formatChartPrice(value: number, siblings: readonly number[] = []) {
  if (!Number.isFinite(value)) return "—";
  const source = numberDecimal(value);
  const short = boundedDecimal(source, "short", -9, 9) ?? "—";
  const collides = siblings.some(
    (sibling) =>
      sibling !== value && boundedDecimal(numberDecimal(sibling), "short", -9, 9) === short,
  );
  return collides
    ? formatPrice(
        source,
        "long",
        siblings.filter((sibling) => sibling !== value).map(numberDecimal),
      )
    : short;
}

function numberDecimal(value: number) {
  if (value === 0) return "0";
  const source = value.toString();
  if (!/[eE]/.test(source)) return source;
  const [coefficient = "0", exponentText = "0"] = source.toLowerCase().split("e");
  const exponent = Number(exponentText);
  const negative = coefficient.startsWith("-");
  const unsigned = negative ? coefficient.slice(1) : coefficient;
  const [whole = "0", fraction = ""] = unsigned.split(".");
  const digits = (whole + fraction).replace(/^0+/, "") || "0";
  const point = whole.length + exponent;
  const expanded =
    point <= 0
      ? `0.${"0".repeat(-point)}${digits}`
      : point >= digits.length
        ? digits + "0".repeat(point - digits.length)
        : `${digits.slice(0, point)}.${digits.slice(point)}`;
  return (negative ? "-" : "") + expanded;
}

export function formatChartTickmarks(values: readonly number[]) {
  const sorted = [...new Set(values.filter(Number.isFinite))].sort((a, b) => a - b);
  const differences: number[] = [];
  for (let index = 1; index < sorted.length; index++) {
    const difference = Math.abs((sorted[index] ?? 0) - (sorted[index - 1] ?? 0));
    if (difference > 0) differences.push(difference);
  }
  differences.sort((a, b) => a - b);
  const spacing = differences[Math.floor(differences.length / 2)] ?? Number.POSITIVE_INFINITY;
  // Repeated tick subtraction accumulates error beyond a few machine epsilons.
  // One trillionth of the spacing is display noise, relative even on tiny-price axes.
  const tolerance = Number.isFinite(spacing) ? spacing * 1e-12 : 0;
  const normalized = values.map((value) => (Math.abs(value) <= tolerance ? 0 : value));
  const short = normalized.map((value) => formatChartPrice(value));
  if (new Set(short).size === new Set(normalized).size) return short;
  const siblings = normalized.filter(Number.isFinite).map(numberDecimal);
  return normalized.map((value) =>
    Number.isFinite(value) ? formatPrice(numberDecimal(value), "long", siblings) : "—",
  );
}

export function formatAgo(value: string | null | undefined, now = Date.now()) {
  if (!value) return "never";
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "unknown";
  const seconds = Math.max(0, Math.floor((now - time) / 1_000));
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${String(seconds)}s ago`;
  if (seconds < 3_600) return `${String(Math.floor(seconds / 60))}m ago`;
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3_600))}h ago`;
  return `${String(Math.floor(seconds / 86_400))}d ago`;
}

export function formatWhen(value: string | null | undefined, now = Date.now()) {
  if (!value) return "not scheduled";
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "unknown";
  if (time <= now) return formatAgo(value, now);
  const seconds = Math.ceil((time - now) / 1_000);
  if (seconds < 60) return `in ${String(seconds)}s`;
  if (seconds < 3_600) return `in ${String(Math.ceil(seconds / 60))}m`;
  return `in ${String(Math.ceil(seconds / 3_600))}h`;
}

/** A 24h HH:MM clock reading, local time (journal rows, design 55 — grouped by day, not "ago"). */
export function formatClockTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** The journal's day-group header text (design 55: "Today" / "Yesterday · 14 Oct" / "12 Oct"). */
export function formatJournalDay(value: string | null | undefined, now = Date.now()): string {
  if (!value) return "Unknown day";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown day";
  const startOfDay = (ms: number) => {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const diffDays = Math.round((startOfDay(now) - startOfDay(date.getTime())) / 86_400_000);
  const dayMonth = date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return `Yesterday · ${dayMonth}`;
  return dayMonth;
}

/** The journal day-group key: items on the same local calendar day share this value. */
export function journalDayKey(value: string): number {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Sign of an exact decimal string without floating arithmetic; "unknown" when unavailable. */
export function signTone(value: string | null | undefined) {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) return "unknown";
  try {
    const [whole = "0", fraction = ""] = value.replace(/^-/, "").split(".");
    const magnitude = BigInt(`${whole}${fraction}` || "0");
    if (magnitude === 0n) return "";
    return value.startsWith("-") ? "negative" : "positive";
  } catch {
    return "unknown";
  }
}

/** Rounds an exact decimal string to `places` decimals, half-up, without floating arithmetic. */
function hundredthsOf(unsigned: string, places = 2) {
  const [whole = "0", fraction = ""] = unsigned.split(".");
  const nonZero = /[1-9]/.test(unsigned);
  const hundredths =
    BigInt(whole) * 10n ** BigInt(places) +
    BigInt(fraction.slice(0, places).padEnd(places, "0")) +
    (fraction.charAt(places) >= "5" ? 1n : 0n);
  return { hundredths, nonZero };
}

/** Signed percent with two decimals, half-up; a non-zero value below 0.005% shows "<0.01%". */
export function formatSignedPercent(value: string | null | undefined) {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) return "—";
  const negative = value.startsWith("-");
  const unsigned = value.replace(/^-/, "");
  const { hundredths, nonZero } = hundredthsOf(unsigned);
  if (hundredths === 0n && nonZero) return `${negative ? "-" : "+"}<0.01%`;
  const padded = hundredths.toString().padStart(3, "0");
  const integer = groupInteger(padded.slice(0, -2));
  return `${hundredths === 0n ? "" : negative ? "-" : "+"}${integer}.${padded.slice(-2)}%`;
}

/**
 * Unsigned percent with two decimals, half-up (a negative input keeps its "-" but never gains a
 * "+"); a non-zero value below 0.005% shows "<0.01%". For money-derived rates such as fee APR.
 */
export function formatPercent2(value: string | null | undefined) {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) return "—";
  const negative = value.startsWith("-");
  const unsigned = value.replace(/^-/, "");
  const { hundredths, nonZero } = hundredthsOf(unsigned);
  if (hundredths === 0n && nonZero) return `${negative ? "-" : ""}<0.01%`;
  const padded = hundredths.toString().padStart(3, "0");
  const integer = groupInteger(padded.slice(0, -2));
  return `${hundredths === 0n ? "" : negative ? "-" : ""}${integer}.${padded.slice(-2)}%`;
}

/**
 * Unsigned percent with three decimals, half-up; a non-zero value below 0.0005% shows "<0.001%".
 * Three rather than two because the realized fee return is checked against a forecast at short
 * horizons, where an hour's worth of fees is a few thousandths of a percent and two decimals
 * would quantize away the very difference the operator is looking at.
 */
export function formatPercent3(value: string | null | undefined) {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) return "—";
  const negative = value.startsWith("-");
  const unsigned = value.replace(/^-/, "");
  const { hundredths: thousandths, nonZero } = hundredthsOf(unsigned, 3);
  if (thousandths === 0n && nonZero) return `${negative ? "-" : ""}<0.001%`;
  const padded = thousandths.toString().padStart(4, "0");
  const integer = groupInteger(padded.slice(0, -3));
  return `${thousandths === 0n ? "" : negative ? "-" : ""}${integer}.${padded.slice(-3)}%`;
}

/**
 * An elapsed span as "1h 32m" — the exact window a measured return covers. Distinct from
 * `formatAgo`, whose single rounded unit ("1h ago") hides the minutes that decide whether a
 * return matches its forecast.
 */
export function formatSpan(ms: number | null | undefined) {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return minutes % 60 === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(minutes % 60)}m`;
  const days = Math.floor(hours / 24);
  return hours % 24 === 0 ? `${String(days)}d` : `${String(days)}d ${String(hours % 24)}h`;
}

/** Truncates (never rounds) an exact decimal string to at most `digits` fraction digits. */
function truncatedDecimal(value: string | null | undefined, digits: number): string | null {
  if (!value || !/^-?\d+(?:\.\d+)?$/.test(value)) return null;
  const negative = value.startsWith("-");
  const unsigned = value.replace(/^-/, "");
  const [whole = "0", fraction = ""] = unsigned.split(".");
  const truncated = fraction.slice(0, digits);
  const decimal = truncated ? `${whole}.${truncated}` : whole;
  return `${negative ? "-" : ""}${decimal}`;
}

/**
 * A native amount truncated to at most 4 fraction digits, never rounded or padded; "—" when
 * unavailable. Distinct from `formatSol`, which keeps full precision for the audit trail. The
 * "Sol" in the name is historical (Task 5 assumed funding was always SOL); this formats any
 * funding-mint or pool-mint amount the caller passes it (Task 8 generalizes to arbitrary mints).
 */
export function formatSolShort(value: string | null | undefined): string {
  return truncatedDecimal(value, 4) ?? "—";
}

/** `formatSolShort` with an explicit "+" on a strictly positive amount; same historical name. */
export function formatSignedSolShort(value: string | null | undefined): string {
  const formatted = formatSolShort(value);
  if (formatted === "—") return "—";
  return signTone(value) === "positive" ? `+${formatted}` : formatted;
}

/** A price-scale range label truncated to at most 2 fraction digits; "—" when unavailable. */
export function formatRangeLabel(value: string | null | undefined): string {
  return truncatedDecimal(value, 2) ?? "—";
}

/** Signed USD amount, or a signed token amount when a symbol suffix is supplied. */
export function formatSignedAmount(value: string | null | undefined, suffix?: string) {
  const formatted = suffix ? formatToken(value) : formatMoney(value ?? null);
  if (formatted === "Unavailable") return "—";
  const prefix = signTone(value) === "positive" ? "+" : "";
  return `${prefix}${formatted}${suffix ? ` ${suffix}` : ""}`;
}

export function shorten(value: string, size = 5) {
  return value.length > size * 2 + 1 ? `${value.slice(0, size)}…${value.slice(-size)}` : value;
}

export function operationLabel(state: string) {
  const labels: Record<string, string> = {
    queued: "Queued",
    approved: "Approved",
    preparing: "Preparing",
    signed_prepared: "Signed and ready to send",
    sent: "Sent",
    confirmed: "Provisionally confirmed",
    finalized: "Finalized",
    completed: "Completed",
    blocked: "Action required",
    failed: "Failed",
    unknown: "Outcome unknown",
  };
  return labels[state] ?? state;
}

/** Funding presentation only; never feed these rounded strings back into authorization. */
export function formatFundingUsd(value: string | null | undefined): string {
  return formatUsd(value);
}

export function formatUsd(
  value: string | null | undefined,
  length: FormatLength = "short",
): string {
  if (!value) return "Unavailable";
  const parsed = parseDecimalString(value);
  if (!parsed) return "Unavailable";
  if (length === "long") {
    const formatted = boundedDecimal(value, "long", -9, 15);
    return formatted
      ? `${parsed.negative ? "-$" : "$"}${formatted.replace(/^-/, "")}`
      : "Unavailable";
  }
  const unsigned = value.replace(/^-/, "");
  const [whole, fraction = ""] = unsigned.split(".");
  const positive = !parsed.zero;
  const fractionalCents = fraction.slice(0, 2).padEnd(2, "0");
  if (positive && BigInt(whole) === 0n && fractionalCents === "00")
    return `${parsed.negative ? "-" : ""}<$0.01`;
  const rounded = fraction.charAt(2) >= "5" ? 1n : 0n;
  const cents = BigInt(whole) * 100n + BigInt(fractionalCents) + rounded;
  const padded = cents.toString().padStart(3, "0");
  const ordinary = `${groupInteger(padded.slice(0, -2))}.${padded.slice(-2)}`;
  if (ordinary.length <= 18) return `${parsed.negative && cents !== 0n ? "-" : ""}$${ordinary}`;
  const compact = boundedDecimal(value, "short", -2);
  return compact ? `${parsed.negative ? "-$" : "$"}${compact.replace(/^-/, "")}` : "Unavailable";
}
export function formatFundingToken(value: string | null | undefined): string {
  return formatToken(value);
}

export function formatToken(
  value: string | null | undefined,
  length: FormatLength = "short",
): string {
  return boundedDecimal(value, length) ?? "Unavailable";
}

/**
 * A SOL amount from an exact server decimal string, without padding or added precision.
 * Unavailable stays "—": the browser never substitutes a zero or converts between units.
 */
export function formatSol(value: string | null | undefined): string {
  const formatted = formatToken(value, "long");
  return formatted === "Unavailable" ? "—" : `${formatted} SOL`;
}
